import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import {
  FiChevronDown,
  FiChevronUp,
  FiCreditCard,
  FiMapPin,
  FiMic,
  FiShield,
} from 'react-icons/fi';
import TopBar from '../components/TopBar';
import layoutStyles from '../styles/layout.module.css';
import styles from './Payment.module.css';
import { db, functions } from '../firebase';
import { UserContext } from '../App';
import { deriveActiveConnectStatus } from '../utils/stripeConnect';
import {
  getArtistApplicationStatus,
  getRoleSubscription,
  isArtistApproved,
  isRoleSubscriptionActive,
  ROLE_ARTIST,
} from '../utils/roles';

const CONNECT_COUNTRIES = [
  { code: 'CA', label: 'Canada', enabled: true },
  { code: 'US', label: 'United States', enabled: false },
];

function formatConnectState(status) {
  if (!status.accountId) return 'Not connected';
  if (status.canCharge && status.canPayout) return 'Fully enabled';
  if (status.canCharge) return 'Can charge';
  return 'Setup required';
}

function describeConnectNotice(status) {
  if (!status?.accountId) return 'Stripe Express account created. Finish setup to accept payments.';
  if (status.canCharge && status.canPayout) return 'Stripe Express is fully enabled for sales and payouts.';
  if (status.canCharge) return 'Stripe Express can accept marketplace charges. Payouts will stay held until payout setup is completed.';
  return 'Stripe Express account updated. You can keep selling, but payouts stay held until the remaining requirements are completed.';
}

function formatSubscriptionStatus(subscription, active) {
  if (active) return subscription?.status || 'active';
  return 'inactive';
}

function createApplicationForm(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const draft = user?.artistApplicationDraft || {};
  return {
    fullName: draft.fullName || name || '',
    artistOrStageName: draft.artistOrStageName || '',
    email: draft.email || user?.email || '',
    phone: draft.phone || '',
    hasAgent: Boolean(draft.hasAgent),
    agentName: draft.agentName || '',
    agentEmail: draft.agentEmail || '',
    agentPhone: draft.agentPhone || '',
    socialLinksText: draft.socialLinksText || '',
    bestContactMethod: draft.bestContactMethod || 'email',
    notes: draft.notes || '',
  };
}

export default function Payment() {
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const connectStatus = useMemo(() => deriveActiveConnectStatus(appUser), [appUser]);
  const lockedConnectCountry = String(appUser?.connect?.country || '').toUpperCase();
  const savedConnectCountry = String(appUser?.settings?.country || '').toUpperCase();
  const artistApprovalApproved = isArtistApproved(appUser);
  const artistApplicationStatus = getArtistApplicationStatus(appUser);
  const artistSubscriptionActive = isRoleSubscriptionActive(appUser, ROLE_ARTIST);
  const artistSubscription = getRoleSubscription(appUser, ROLE_ARTIST);

  const [cards, setCards] = useState([]);
  const [defaultCardId, setDefaultCardId] = useState('');
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState('');
  const [cardActionError, setCardActionError] = useState('');
  const [busyCardAction, setBusyCardAction] = useState({ type: '', paymentMethodId: '' });

  const [subscriptionBusyRole, setSubscriptionBusyRole] = useState('');
  const [subscriptionError, setSubscriptionError] = useState('');
  const [cancelRole, setCancelRole] = useState('');
  const [cancelText, setCancelText] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelPromptRole, setCancelPromptRole] = useState('');

  const [applicationForm, setApplicationForm] = useState(() => createApplicationForm(appUser));
  const [applicationBusy, setApplicationBusy] = useState(false);
  const [applicationError, setApplicationError] = useState('');
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  const [connectBusy, setConnectBusy] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [connectNotice, setConnectNotice] = useState('');
  const [refreshResult, setRefreshResult] = useState(null);
  const [connectCountry, setConnectCountry] = useState('');

  const liveConnectState = {
    ...connectStatus,
    accountId: refreshResult?.accountId ?? connectStatus.accountId,
    canCharge: refreshResult?.canCharge ?? connectStatus.canCharge,
    canPayout: refreshResult?.canPayout ?? connectStatus.canPayout,
    chargesEnabled: refreshResult?.chargesEnabled ?? connectStatus.chargesEnabled,
    payoutsEnabled: refreshResult?.payoutsEnabled ?? connectStatus.payoutsEnabled,
    statusPhase: refreshResult?.statusPhase ?? connectStatus.statusPhase,
    issues: Array.isArray(refreshResult?.issues) ? refreshResult.issues : connectStatus.issues,
    requirements: refreshResult?.requirements ?? appUser?.connect?.requirements ?? {},
    disabledReason: refreshResult && typeof refreshResult.disabledReason === 'string'
      ? refreshResult.disabledReason
      : connectStatus.disabledReason,
  };
  const liveHasConnectAccount = Boolean(liveConnectState.accountId);
  const liveIsFullyEnabled = Boolean(liveConnectState.canCharge && liveConnectState.canPayout);
  const liveNeedsSetup = Boolean(liveHasConnectAccount && !liveIsFullyEnabled);

  useEffect(() => {
    setApplicationForm(createApplicationForm(appUser));
  }, [appUser]);

  useEffect(() => {
    if (artistApplicationStatus === 'rejected') {
      setShowApplicationForm(true);
    }
    if (artistApplicationStatus === 'pending' || artistApprovalApproved) {
      setShowApplicationForm(false);
    }
  }, [artistApplicationStatus, artistApprovalApproved]);

  useEffect(() => {
    if (liveHasConnectAccount) return;
    const preferred = savedConnectCountry === 'CA' ? savedConnectCountry : 'CA';
    setConnectCountry((current) => current || preferred);
  }, [liveHasConnectAccount, savedConnectCountry]);

  const accessRows = useMemo(() => ([
    {
      key: 'fan',
      title: 'Fan Access',
      blurb: 'Free account access for buying, collecting, and resale activity.',
      status: 'Free',
      active: true,
      actionLabel: 'Included',
    },
    {
      key: ROLE_ARTIST,
      title: 'Artist Access',
      blurb: artistApprovalApproved
        ? 'Publish collectibles, maintain your artist page, and manage your fan-facing artist presence.'
        : 'Apply for review first. Once approved, you can subscribe and unlock creator tools.',
      status: artistSubscriptionActive
        ? formatSubscriptionStatus(artistSubscription, true)
        : artistApprovalApproved
          ? 'inactive'
          : artistApplicationStatus === 'pending'
            ? 'under review'
            : artistApplicationStatus === 'rejected'
              ? 'reapply required'
              : 'application required',
      active: artistSubscriptionActive,
      actionLabel: artistSubscriptionActive ? 'Cancel Artist Access' : 'Subscribe to Artist Access',
    },
  ]), [artistApplicationStatus, artistApprovalApproved, artistSubscription, artistSubscriptionActive]);

  const reloadCards = useCallback(async () => {
    if (!appUser?.id || !appUser?.stripeCustomerId) {
      setCards([]);
      setDefaultCardId('');
      setCardsError(appUser?.id ? 'No payment methods on file yet.' : '');
      return;
    }
    try {
      setCardsLoading(true);
      setCardsError('');
      const callable = httpsCallable(functions, 'listPaymentMethods');
      const resp = await callable({});
      const methods = Array.isArray(resp?.data?.methods) ? resp.data.methods : [];
      setCards(methods);
      setDefaultCardId(String(resp?.data?.defaultPaymentMethodId || ''));
      if (!methods.length) setCardsError('No payment methods on file yet.');
    } catch (err) {
      setCards([]);
      setDefaultCardId('');
      setCardsError(err?.message || 'Unable to load cards right now.');
    } finally {
      setCardsLoading(false);
    }
  }, [appUser?.id, appUser?.stripeCustomerId]);

  const refreshStatus = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setConnectBusy(true);
      setConnectError('');
    }
    try {
      const callable = httpsCallable(functions, 'refreshStripeConnectStatus');
      const result = await callable({});
      const data = result?.data || {};
      setRefreshResult((prev) => ({ ...(prev || {}), ...data }));
      if (!silent) setConnectNotice(describeConnectNotice(data));
    } catch (err) {
      if (!silent) setConnectError(err?.message || 'Unable to refresh Stripe Express status.');
    } finally {
      if (!silent) setConnectBusy(false);
    }
  }, []);

  useEffect(() => {
    reloadCards();
  }, [reloadCards]);

  useEffect(() => {
    if (!appUser?.id) return;
    const params = new URLSearchParams(location.search);
    const subscribed = String(params.get('subscribed') || '').trim();
    const connectReturn = String(params.get('connect_return') || '').trim();

    if (subscribed) {
      setConnectNotice(`${subscribed.charAt(0).toUpperCase()}${subscribed.slice(1)} subscription activated.`);
      navigate('/payment', { replace: true });
      return;
    }

    if (!connectReturn) return;

    let active = true;
    (async () => {
      setConnectBusy(true);
      setConnectError('');
      try {
        if (!active) return;
        setConnectNotice('Stripe Express account updated. Verifying charge and payout status...');
        await refreshStatus({ silent: true });
        navigate('/payment', { replace: true });
      } catch (err) {
        if (!active) return;
        setConnectError(err?.message || 'Unable to refresh Stripe Express setup.');
      } finally {
        if (active) setConnectBusy(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [appUser?.id, location.search, navigate, refreshStatus]);

  const handleSetDefault = useCallback(async (paymentMethodId) => {
    try {
      setBusyCardAction({ type: 'default', paymentMethodId });
      setCardActionError('');
      const callable = httpsCallable(functions, 'setDefaultPaymentMethod');
      await callable({ paymentMethodId });
      await reloadCards();
    } catch (err) {
      setCardActionError(err?.message || 'Unable to set default card.');
    } finally {
      setBusyCardAction({ type: '', paymentMethodId: '' });
    }
  }, [reloadCards]);

  const handleRemoveCard = useCallback(async (paymentMethodId) => {
    try {
      setBusyCardAction({ type: 'remove', paymentMethodId });
      setCardActionError('');
      const callable = httpsCallable(functions, 'detachPaymentMethod');
      await callable({ paymentMethodId });
      await reloadCards();
    } catch (err) {
      setCardActionError(err?.message || 'Unable to remove card.');
    } finally {
      setBusyCardAction({ type: '', paymentMethodId: '' });
    }
  }, [reloadCards]);

  const startSubscription = useCallback(async (role) => {
    try {
      setSubscriptionBusyRole(role);
      setSubscriptionError('');
      const callable = httpsCallable(functions, 'createSubscriptionCheckoutSession');
      const baseUrl = window.location.origin;
      const result = await callable({
        role,
        successUrl: `${baseUrl}/payment?subscribed=${role}`,
        cancelUrl: `${baseUrl}/payment`,
      });
      const url = result?.data?.url;
      if (!url) throw new Error('Unable to start subscription checkout.');
      window.location.assign(url);
    } catch (err) {
      setSubscriptionError(err?.message || `Unable to start ${role} subscription.`);
      setSubscriptionBusyRole('');
    }
  }, []);

  const handleCancelSubscription = useCallback(async () => {
    if (!cancelRole || cancelText.trim().toUpperCase() !== 'CANCEL') return;
    try {
      setCancelBusy(true);
      setSubscriptionError('');
      const callable = httpsCallable(functions, 'cancelSubscription');
      await callable({ role: cancelRole });
      setCancelRole('');
      setCancelPromptRole('');
      setCancelText('');
      setConnectNotice(`${cancelRole.charAt(0).toUpperCase()}${cancelRole.slice(1)} subscription will end at period close.`);
    } catch (err) {
      setSubscriptionError(err?.message || 'Unable to cancel subscription.');
    } finally {
      setCancelBusy(false);
    }
  }, [cancelRole, cancelText]);

  const handleSubmitApplication = useCallback(async (event) => {
    event.preventDefault();
    try {
      setApplicationBusy(true);
      setApplicationError('');
      setSubscriptionError('');
      const callable = httpsCallable(functions, 'submitArtistAccessApplication');
      await callable(applicationForm);
      setShowApplicationForm(false);
      setConnectNotice('Thanks. We will review your application and get back to you within 2 business days.');
    } catch (err) {
      setApplicationError(err?.message || 'Unable to submit your artist access application.');
    } finally {
      setApplicationBusy(false);
    }
  }, [applicationForm]);

  const startConnect = useCallback(async () => {
    try {
      if (!connectCountry) throw new Error('Choose the country for your Stripe Express account before continuing.');
      setConnectBusy(true);
      setConnectError('');
      const baseUrl = window.location.origin;
      if (appUser?.id) {
        await setDoc(doc(db, 'users', appUser.id), {
          settings: {
            ...(appUser?.settings || {}),
            country: connectCountry,
          },
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }
      const callable = httpsCallable(functions, 'createStripeExpressAccountLink');
      const result = await callable({
        country: connectCountry,
        refreshUrl: `${baseUrl}/payment`,
        returnUrl: `${baseUrl}/payment?connect_return=1`,
      });
      const url = result?.data?.url;
      if (!url) throw new Error('Unable to generate Stripe Express onboarding URL.');
      window.location.assign(url);
    } catch (err) {
      setConnectError(err?.message || 'Unable to start Stripe Express setup.');
      setConnectBusy(false);
    }
  }, [appUser?.id, appUser?.settings, connectCountry]);

  const manageConnect = useCallback(async () => {
    try {
      setConnectBusy(true);
      setConnectError('');
      const baseUrl = window.location.origin;
      const callable = httpsCallable(functions, 'createStripeExpressAccountLink');
      const result = await callable({
        mode: 'update',
        refreshUrl: `${baseUrl}/payment`,
        returnUrl: `${baseUrl}/payment?connect_return=1`,
      });
      const url = result?.data?.url;
      if (!url) throw new Error('Unable to generate Stripe Express management link.');
      window.location.assign(url);
    } catch (err) {
      setConnectError(err?.message || 'Unable to manage Stripe Express account.');
      setConnectBusy(false);
    }
  }, []);

  const openExpressDashboard = useCallback(async () => {
    try {
      if (!liveConnectState.accountId || !liveConnectState.canCharge) {
        throw new Error('Complete Stripe Express onboarding before opening the Express dashboard.');
      }
      setConnectBusy(true);
      setConnectError('');
      const callable = httpsCallable(functions, 'createStripeExpressDashboardLink');
      const result = await callable({});
      const url = result?.data?.url;
      if (!url) throw new Error('Unable to open Stripe Express dashboard.');
      window.location.assign(url);
    } catch (err) {
      setConnectError(err?.message || 'Unable to open Stripe Express dashboard.');
      setConnectBusy(false);
    }
  }, [liveConnectState.accountId, liveConnectState.canCharge]);

  const renderArtistAccessActions = () => {
    if (artistSubscriptionActive) {
      return cancelPromptRole !== ROLE_ARTIST ? (
        <button
          type="button"
          className={styles.dangerAction}
          onClick={() => { setCancelRole(ROLE_ARTIST); setCancelPromptRole(ROLE_ARTIST); setCancelText(''); }}
          disabled={cancelBusy || subscriptionBusyRole === ROLE_ARTIST || connectBusy}
        >
          Cancel Artist Access
        </button>
      ) : (
        <div className={styles.cancelPanel}>
          <p>Type CANCEL to confirm.</p>
          <input value={cancelText} onChange={(event) => setCancelText(event.target.value)} placeholder="CANCEL" />
          <div className={styles.cancelActions}>
            <button
              type="button"
              className={styles.secondaryAction}
              onClick={() => { setCancelRole(''); setCancelPromptRole(''); setCancelText(''); }}
              disabled={cancelBusy}
            >
              Keep Access
            </button>
            <button
              type="button"
              className={styles.dangerAction}
              disabled={cancelBusy || cancelText.trim().toUpperCase() !== 'CANCEL' || connectBusy}
              onClick={handleCancelSubscription}
            >
              {cancelBusy ? 'Canceling…' : 'Confirm Cancellation'}
            </button>
          </div>
        </div>
      );
    }

    if (artistApprovalApproved) {
      return (
        <button
          type="button"
          className={styles.primaryAction}
          onClick={() => startSubscription(ROLE_ARTIST)}
          disabled={subscriptionBusyRole === ROLE_ARTIST || connectBusy || cancelBusy}
        >
          {subscriptionBusyRole === ROLE_ARTIST ? 'Starting…' : 'Subscribe to Artist Access'}
        </button>
      );
    }

    if (artistApplicationStatus === 'pending') {
      return (
        <div className={styles.applicationStateCard}>
          <p className={styles.subscriptionMeta}>Your artist access application is under review. We will get back to you within 2 business days.</p>
        </div>
      );
    }

    return (
      <div className={styles.applicationWrap}>
        <div className={styles.applicationIntro}>
          <button
            type="button"
            className={styles.expandAction}
            onClick={() => setShowApplicationForm((prev) => !prev)}
            disabled={applicationBusy || connectBusy || cancelBusy}
          >
            <FiMic />
            {artistApplicationStatus === 'rejected'
              ? (showApplicationForm ? 'Hide Reapplication Form' : 'Reapply for Artist Access')
              : (showApplicationForm ? 'Hide Application Form' : 'Apply for Artist Access')}
            {showApplicationForm ? <FiChevronUp /> : <FiChevronDown />}
          </button>
          {!showApplicationForm ? (
            <p className={styles.subscriptionMeta}>Start with a short application. Once approved, you can subscribe and continue setup.</p>
          ) : null}
        </div>

        {showApplicationForm ? (
          <form className={styles.applicationForm} onSubmit={handleSubmitApplication}>
            <label>
              <span>Full Name</span>
              <input
                value={applicationForm.fullName}
                onChange={(event) => setApplicationForm((prev) => ({ ...prev, fullName: event.target.value }))}
                required
              />
            </label>
            <label>
              <span>Artist / Stage Name</span>
              <input
                value={applicationForm.artistOrStageName}
                onChange={(event) => setApplicationForm((prev) => ({ ...prev, artistOrStageName: event.target.value }))}
                required
              />
            </label>
            <div className={styles.applicationGrid}>
              <label>
                <span>Contact Email</span>
                <input
                  type="email"
                  value={applicationForm.email}
                  onChange={(event) => setApplicationForm((prev) => ({ ...prev, email: event.target.value }))}
                  required
                />
              </label>
              <label>
                <span>Phone Number</span>
                <input
                  value={applicationForm.phone}
                  onChange={(event) => setApplicationForm((prev) => ({ ...prev, phone: event.target.value }))}
                  required
                />
              </label>
              <label>
                <span>Agent / Manager</span>
                <select
                  value={applicationForm.hasAgent ? 'yes' : 'no'}
                  onChange={(event) => setApplicationForm((prev) => ({
                    ...prev,
                    hasAgent: event.target.value === 'yes',
                    bestContactMethod: event.target.value === 'yes' ? prev.bestContactMethod : (prev.bestContactMethod === 'agent' ? 'email' : prev.bestContactMethod),
                  }))}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
              <label>
                <span>Best Contact Method</span>
                <select
                  value={applicationForm.bestContactMethod}
                  onChange={(event) => setApplicationForm((prev) => ({ ...prev, bestContactMethod: event.target.value }))}
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="either">Either</option>
                  {applicationForm.hasAgent ? <option value="agent">Agent / Manager</option> : null}
                </select>
              </label>
            </div>

            {applicationForm.hasAgent ? (
              <div className={styles.applicationGrid}>
                <label>
                  <span>Agent / Manager Name</span>
                  <input
                    value={applicationForm.agentName}
                    onChange={(event) => setApplicationForm((prev) => ({ ...prev, agentName: event.target.value }))}
                  />
                </label>
                <label>
                  <span>Agent / Manager Email</span>
                  <input
                    type="email"
                    value={applicationForm.agentEmail}
                    onChange={(event) => setApplicationForm((prev) => ({ ...prev, agentEmail: event.target.value }))}
                  />
                </label>
                <label>
                  <span>Agent / Manager Phone</span>
                  <input
                    value={applicationForm.agentPhone}
                    onChange={(event) => setApplicationForm((prev) => ({ ...prev, agentPhone: event.target.value }))}
                  />
                </label>
              </div>
            ) : null}

            <label>
              <span>Social Links or Handles</span>
              <textarea
                rows={3}
                value={applicationForm.socialLinksText}
                onChange={(event) => setApplicationForm((prev) => ({ ...prev, socialLinksText: event.target.value }))}
                placeholder="@artistname, instagram.com/artistname, soundcloud.com/artistname"
              />
            </label>
            <label>
              <span>Short Note</span>
              <textarea
                rows={4}
                value={applicationForm.notes}
                onChange={(event) => setApplicationForm((prev) => ({ ...prev, notes: event.target.value }))}
                placeholder="Tell us who you are and what you plan to release on Razzberry."
              />
            </label>

            <p className={styles.subscriptionMeta}>We review applications manually and will get back to you within 2 business days.</p>
            <div className={styles.compactActions}>
              <button type="submit" className={styles.primaryAction} disabled={applicationBusy || connectBusy || cancelBusy}>
                {applicationBusy ? 'Submitting…' : artistApplicationStatus === 'rejected' ? 'Reapply for Artist Access' : 'Submit Application'}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    );
  };

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => navigate('/fan/settings')} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <section className={styles.heroCard}>
          <div className={styles.heroCopy}>
            <span className={styles.eyebrow}>Payments</span>
            <h1>Stripe Payment</h1>
            <p>One dashboard for buyer cards, artist applications, subscriptions, and the Stripe Express account required before you can publish or resell.</p>
          </div>
          <div className={styles.heroActions}>
            <div className={styles.heroMetric}>
              <div className={styles.metricIcon}><FiShield /></div>
              <span>Shared Payout Account</span>
              <strong>{formatConnectState(liveConnectState)}</strong>
            </div>
            <div className={styles.heroMetric}>
              <div className={styles.metricIcon}><FiCreditCard /></div>
              <span>Saved Cards</span>
              <strong>{cards.length}</strong>
            </div>
          </div>
        </section>

        {(connectNotice || connectError || subscriptionError || applicationError) ? (
          <section className={styles.noticeCard}>
            {connectNotice ? <p className={styles.noticeText}>{connectNotice}</p> : null}
            {connectError ? <p className={styles.errorText}>{connectError}</p> : null}
            {subscriptionError ? <p className={styles.errorText}>{subscriptionError}</p> : null}
            {applicationError ? <p className={styles.errorText}>{applicationError}</p> : null}
          </section>
        ) : null}

        <section className={styles.sectionGrid}>
          <section className={`${styles.card} ${styles.featureCard}`.trim()}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitleWrap}>
                <div className={styles.sectionIcon}><FiCreditCard /></div>
                <div>
                <span className={styles.sectionEyebrow}>Buyer Payments</span>
                <h2>Saved Cards</h2>
                </div>
              </div>
            </div>
            <p className={styles.sectionCopy}>Use the purchase preview page to add a new card during checkout. This page keeps your saved cards organized.</p>
            {cardsLoading ? <p className={styles.stateText}>Loading saved cards…</p> : null}
            {!cardsLoading && cardsError ? <p className={styles.stateText}>{cardsError}</p> : null}
            {cards.length ? (
              <div className={styles.cardsList}>
                {cards.map((card) => (
                  <div key={card.id} className={styles.cardRow}>
                    <div>
                      <strong>{card.brand?.toUpperCase() || 'CARD'} ending in {card.last4}</strong>
                      <p>Expires {card.expMonth}/{card.expYear}</p>
                    </div>
                    <div className={styles.cardActions}>
                      {defaultCardId === card.id ? (
                        <span className={styles.defaultBadge}>Default</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(card.id)}
                          disabled={Boolean(busyCardAction.paymentMethodId)}
                        >
                          {busyCardAction.type === 'default' && busyCardAction.paymentMethodId === card.id ? 'Setting…' : 'Set Default'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveCard(card.id)}
                        disabled={Boolean(busyCardAction.paymentMethodId)}
                      >
                        {busyCardAction.type === 'remove' && busyCardAction.paymentMethodId === card.id ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            {cardActionError ? <p className={styles.errorText}>{cardActionError}</p> : null}
          </section>

          <section className={`${styles.card} ${styles.featureCard}`.trim()}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitleWrap}>
                <div className={styles.sectionIcon}><FiMic /></div>
                <div>
                <span className={styles.sectionEyebrow}>Access</span>
                <h2>Access Tiers</h2>
                </div>
              </div>
            </div>
            <p className={styles.sectionCopy}>Your account always includes fan access. Artist tools now open after application review and an active Artist Access subscription.</p>
            <div className={styles.subscriptionList}>
              {accessRows.map((item) => (
                <div key={item.key} className={styles.subscriptionCard}>
                  <div className={styles.subscriptionHeader}>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.blurb}</p>
                    </div>
                    <span className={`${styles.statusPill} ${item.active ? styles.statusReady : styles.statusMuted}`.trim()}>{item.status}</span>
                  </div>
                  <p className={styles.subscriptionMeta}>Status: {item.status}</p>
                  {item.key === 'fan' ? (
                    <button type="button" className={styles.secondaryAction} disabled>
                      Included
                    </button>
                  ) : (
                    renderArtistAccessActions()
                  )}
                </div>
              ))}
            </div>
            {!artistApprovalApproved && artistApplicationStatus === 'rejected' ? (
              <p className={styles.stateText}>Your previous application was not approved. Update the details below and submit again.</p>
            ) : null}
          </section>
        </section>

        <section className={`${styles.card} ${styles.featureCard}`.trim()}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleWrap}>
              <div className={styles.sectionIcon}><FiMapPin /></div>
              <div>
              <span className={styles.sectionEyebrow}>Payout Account</span>
              <h2>Shared Stripe Express</h2>
              </div>
            </div>
            <div className={styles.connectHeaderActions}>
              <span className={`${styles.statusPill} ${liveConnectState.canCharge ? styles.statusReady : liveConnectState.accountId ? styles.statusWarn : styles.statusMuted}`.trim()}>
                {formatConnectState(liveConnectState)}
              </span>
            </div>
          </div>
          <p className={styles.sectionCopy}>This one Stripe Express account lets you publish and sell as soon as charging is enabled. For now, Connect onboarding is available only in Canada.</p>

          <div className={styles.connectGrid}>
            <div className={styles.connectSummary}>
              <div className={styles.summaryRow}>
                <span>Account ID</span>
                <strong>{liveConnectState.accountId || 'Not connected'}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Can Charge</span>
                <strong>{liveConnectState.canCharge ? 'Enabled' : 'Pending'}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Can Payout</span>
                <strong>{liveConnectState.canPayout ? 'Enabled' : 'Pending'}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Status</span>
                <strong>{formatConnectState(liveConnectState)}</strong>
              </div>
            </div>

            <div className={styles.connectActions}>
              {!liveHasConnectAccount ? (
                <>
                  <label className={styles.sectionEyebrow} htmlFor="paymentConnectCountry">Stripe Account Country</label>
                  <select
                    id="paymentConnectCountry"
                    value={connectCountry}
                    onChange={(event) => setConnectCountry(event.target.value)}
                    disabled={connectBusy}
                  >
                    <option value="" disabled>Select country</option>
                    {CONNECT_COUNTRIES.map((entry) => (
                      <option key={entry.code} value={entry.code} disabled={!entry.enabled}>{entry.label}{entry.enabled ? '' : ' (Unavailable)'}</option>
                    ))}
                  </select>
                  <p className={styles.stateText}>Choose Canada for your Stripe Express account before creating it. The dropdown stays in place, but the United States option is currently unavailable. This choice is locked after account creation.</p>
                  {savedConnectCountry ? <p className={styles.stateText}>Previously saved choice: {savedConnectCountry}.</p> : null}
                  <div className={styles.compactActions}>
                    <button type="button" className={styles.primaryAction} onClick={startConnect} disabled={connectBusy}>
                      {connectBusy ? 'Starting…' : 'Create Stripe Account'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <label className={styles.sectionEyebrow} htmlFor="paymentConnectCountryLocked">Stripe Account Country</label>
                  <input
                    id="paymentConnectCountryLocked"
                    value={lockedConnectCountry || savedConnectCountry}
                    readOnly
                    disabled
                  />
                  <p className={styles.stateText}>Stripe account country is locked after account creation. If the connected account ever needs to change, contact an admin so they can handle it safely.</p>
                  <div className={styles.compactActions}>
                    {liveNeedsSetup ? (
                      <>
                        <button type="button" className={styles.primaryAction} onClick={manageConnect} disabled={connectBusy}>
                          {connectBusy ? 'Opening…' : 'Continue Stripe Setup'}
                        </button>
                        <button type="button" className={styles.secondaryAction} onClick={() => refreshStatus()} disabled={connectBusy}>
                          {connectBusy ? 'Refreshing…' : 'Refresh Status'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" className={styles.primaryAction} onClick={openExpressDashboard} disabled={connectBusy || !liveConnectState.accountId || !liveConnectState.canCharge}>
                          {connectBusy ? 'Opening…' : 'Open Express Dashboard'}
                        </button>
                        <button type="button" className={styles.secondaryAction} onClick={() => refreshStatus()} disabled={connectBusy}>
                          {connectBusy ? 'Refreshing…' : 'Refresh Status'}
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
              {liveConnectState.disabledReason ? (
                <p className={styles.stateText}>Stripe: {String(liveConnectState.disabledReason).replace(/_/g, ' ')}</p>
              ) : null}
              {liveConnectState.issues?.length ? (
                <ul className={styles.issueList}>
                  {liveConnectState.issues.map((item) => (
                    <li key={item}>{String(item).replace(/_/g, ' ')}</li>
                  ))}
                </ul>
              ) : null}
              {liveConnectState.requirements?.currentDeadline ? (
                <p className={styles.stateText}>Current deadline: {new Date(liveConnectState.requirements.currentDeadline * 1000).toLocaleString()}</p>
              ) : null}
            </div>
          </div>
        </section>

        <section className={`${styles.card} ${styles.featureCard}`.trim()}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleWrap}>
              <div className={styles.sectionIcon}><FiShield /></div>
              <div>
              <span className={styles.sectionEyebrow}>Terms</span>
              <h2>Terms &amp; Purchase Conditions</h2>
              </div>
            </div>
          </div>
          <p className={styles.sectionCopy}>Buy flows now surface the same purchase-terms link before checkout. Review the current platform and purchase conditions here.</p>
          <Link className={styles.inlineLink} to="/terms">Open Terms &amp; Purchase Conditions</Link>
        </section>
      </div>
    </div>
  );
}
